// src/pages/Admin/Layout.jsx
import React, { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ActivityMonitor from '../../components/ActivityMonitor';
import authService from '../../services/authService';

const LayoutContainer = styled.div`
  display: flex;
  min-height: 100vh;
  overflow: hidden;
`;

const Sidebar = styled.aside`
  width: 70px;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  color: white;
  box-shadow: 4px 0 12px rgba(102, 126, 234, 0.2);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SidebarHeader = styled.div`
  padding: 20px 0;
  margin-bottom: 20px;
  width: 100%;
  text-align: center;
`;

const SidebarMenu = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  width: 100%;
`;

const SidebarMenuItem = styled.li`
  margin: 0;
  width: 100%;
`;

const SidebarLink = styled(NavLink)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
    transform: translateX(3px);
  }

  &.active {
    background-color: rgba(255, 255, 255, 0.2);
    color: white;
    border-left: 4px solid white;

    &::before {
      content: '';
      position: absolute;
      right: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 0;
      height: 0;
      border-left: 8px solid white;
      border-top: 8px solid transparent;
      border-bottom: 8px solid transparent;
    }
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MainContent = styled.main`
  flex: 1;
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
`;

const Header = styled.header`
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  height: 64px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 32px;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.2);
  flex-shrink: 0;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  margin: 0;
  color: white;
  font-weight: 700;
`;

const LogoutButton = styled.button`
  background-color: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 600;

  &:hover {
    background-color: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

const Content = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  overflow-y: auto;
  height: calc(100vh - 64px);
`;

// Confirmation Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  transform: scale(1);
  animation: modalAppear 0.3s ease-out;

  @keyframes modalAppear {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
`;

const WarningIcon = styled.div`
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  
  svg {
    width: 24px;
    height: 24px;
    color: white;
  }
`;

const ModalTitle = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin: 0;
`;

const ModalMessage = styled.p`
  font-size: 16px;
  color: #7f8c8d;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-size: 14px;

  &.cancel {
    background-color: #ecf0f1;
    color: #7f8c8d;

    &:hover {
      background-color: #d5dbdb;
      transform: translateY(-1px);
    }
  }

  &.confirm {
    background: linear-gradient(135deg, #e74c3c, #c0392b);
    color: white;

    &:hover {
      background: linear-gradient(135deg, #c0392b, #a93226);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(231, 76, 60, 0.4);
    }
  }
`;

const HomeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
    <polyline points="9 22 9 12 15 12 15 22"/>
  </svg>
);

const BrandIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 3a2 2 0 0 0-2 2v14c0 1.1.9 2 2 2h14a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2H5zm0 2h14v14H5V5z"/>
    <path d="M7 7h10v2H7V7z"/>
    <path d="M7 11h10v2H7v-2z"/>
    <path d="M7 15h4v2H7v-2z"/>
  </svg>
);

const ExclamationIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"/>
    <line x1="12" y1="8" x2="12" y2="12"/>
    <line x1="12" y1="16" x2="12.01" y2="16"/>
  </svg>
);

const Layout = () => {
  const navigate = useNavigate();
  const [showLogoutConfirmation, setShowLogoutConfirmation] = useState(false);

  const handleLogoutClick = () => {
    setShowLogoutConfirmation(true);
  };

  const handleLogoutConfirm = () => {
    // Clear all admin tokens directly
    localStorage.removeItem('adminToken');
    localStorage.removeItem('admin');
    localStorage.removeItem('adminTokenExpiry');
    localStorage.removeItem('token'); 

    // Close modal
    setShowLogoutConfirmation(false);

    // Force redirect to admin login
    window.location.href = '/admin-login';
  };

  const handleLogoutCancel = () => {
    setShowLogoutConfirmation(false);
  };

  return (
    <LayoutContainer>
      <ActivityMonitor isAdmin={true} />
      <Sidebar>
        <SidebarHeader>
          <HomeIcon />
        </SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarLink to="/admin-dhobicart/brands" title="Brands">
              <BrandIcon />
            </SidebarLink>
          </SidebarMenuItem>
        </SidebarMenu>
      </Sidebar>

      <MainContent>
        <Header>
          <PageTitle>Admin Dashboard</PageTitle>
          <LogoutButton onClick={handleLogoutClick}>Logout</LogoutButton>
        </Header>

        <Content>
          <Outlet />
        </Content>
      </MainContent>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirmation && (
        <ModalOverlay onClick={handleLogoutCancel}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <WarningIcon>
                <ExclamationIcon />
              </WarningIcon>
              <ModalTitle>Confirm Logout</ModalTitle>
            </ModalHeader>
            <ModalMessage>
              Are you sure you want to logout? You will need to sign in again to access the admin dashboard.
            </ModalMessage>
            <ModalActions>
              <ModalButton className="cancel" onClick={handleLogoutCancel}>
                Cancel
              </ModalButton>
              <ModalButton className="confirm" onClick={handleLogoutConfirm}>
                Yes, Logout
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </LayoutContainer>
  );
};

export default Layout;
